import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Search from "../../../components/Search";
import { setCurrentPage, setSearchText } from "../../../redux/historicalSlice";
import { setHideFilter } from "../../../redux/filtersSlice";
import { RootState } from "../../../redux/store";
import AppTable, { ColumnConfig } from "../../../components/AppTable/AppTable";
import { ReportDefinitionListItemDto } from "../types";
import { SectionPermissionTypeEnum } from "../../../types/SectionPermissionTypesEnum";
import AlertMessage from "../../../components/AlertMessage";
import { useAlert } from "../../../hooks/useAlert";
import {
    deleteLogicReportDefinition,
    exportReportDefinitionsToCsv
} from "../api/reportDefinitionAPI";
import { useReportDefinitions } from "../hooks/useReportDefinitions";
import { SelectItemDto } from "../../../types/SelectItemDto";
import { useModal } from "../../../hooks/useModal";
import { useQueryClient } from "@tanstack/react-query";
import FiltersContainer from "../../../components/Filters/components/FiltersContainer";
import { FilterDto, FilterType } from "../../../types/FilterDto";
import { ReportDefinitionStatusEnum, ReportExecutionModeEnum } from "../types/enums";

export default function ReportDefinitionsList() {
    const { t } = useTranslation(["reportDefinition", "common"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    
    const filters: FilterDto[] = [
        {
            name: 'Status',
            description: t("common:status"),
            type: FilterType.MULTIPLE,
            values: Object.keys(ReportDefinitionStatusEnum)
                .filter((key) => isNaN(Number(key)))
                .map((value) => ({
                    key: ReportDefinitionStatusEnum[value as keyof typeof ReportDefinitionStatusEnum],
                    value: t(`reportDefinition:form.status.${value}`)
                }))
        },
        {
            name: 'ExecutionMode',
            description: t("reportDefinition:list.executionMode"),
            type: FilterType.MULTIPLE,
            values: Object.keys(ReportExecutionModeEnum)
                .filter((key) => isNaN(Number(key)))
                .map((value) => ({
                    key: ReportExecutionModeEnum[value as keyof typeof ReportExecutionModeEnum],
                    value: t(`reportDefinition:form.executionMode.${value}`)
                }))
        },
        {
            name: 'SendingDate',
            description: t("reportDefinition:list.sendingDate"),
            type: FilterType.DATE,
            values: []
        }
    ];

    const currentPage = useSelector((state: RootState) => state.historical.reportDefinitions.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.reportDefinitions.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.reportDefinitions.searchText);

    const assignedToMeToggle = useSelector((state: RootState) => state.filters.reportDefinitions.filterByCurrentUser);
    const queryClient = useQueryClient();

    const [deleteReasonOptions, setDeleteReasonOptions] = useState<SelectItemDto[]>([]);
    const [selectedDeleteReasonId, setSelectedDeleteReasonId] = useState<number | null>(null);
    const [deleteModalError, setDeleteModalError] = useState<string | undefined>(undefined);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isLoadingDeleteReasons, setIsLoadingDeleteReasons] = useState(false);
    const {
        showModal: showDeleteModal,
        modalData: deleteModalData,
        openModal: openDeleteModal,
        closeModal: closeDeleteModalBase,
        currentItem: selectedDeleteItem,
        isLoading: isDeleting,
        setLoading: setIsDeleting
    } = useModal<ReportDefinitionListItemDto>();

    const { data: reportDefinitionsPagination, isLoading: isListLoading, error, refetch } = useReportDefinitions({});

    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const handleSearchInput = () => {
        dispatch(setCurrentPage({ table: "reportDefinitions", page: 1 }));
    };

    const handleClear = () => {
        dispatch(setSearchText({ table: "reportDefinitions", text: "", goFirstPage: true }));
    };

    const columns: ColumnConfig<ReportDefinitionListItemDto>[] = [
        { header: t("common:form.fields.id"), key: "id", render: (item) => <b>{item.id}</b> },
        { header: t("common:form.fields.title"), key: "title" },
        { header: t("reportDefinition:list.executionMode"), key: "executionMode", render: (item) => <>{t(`reportDefinition:form.executionMode.${item.executionMode}`)}</> },
        { header: t("common:form.fields.status"), key: "status", render: (item) => <>{t(`reportDefinition:form.status.${item.status}`)}</> },
        { header: t("reportDefinition:list.sendingDate"), key: "sendingDate" },
        { header: t("reportDefinition:list.usersCount"), key: "usersCount", render: (item) => <>
            {item.status == ReportDefinitionStatusEnum[ReportDefinitionStatusEnum.PendingScheduling].toString() || item.status == ReportDefinitionStatusEnum[ReportDefinitionStatusEnum.InProgress].toString()
                ? <>{item.totalUsersCount}</>
                : <>{`${item.reportedUsersCount} / ${item.totalUsersCount}`}</>
            }
        </> },
        { header: t("reportDefinition:list.contentDeliveryType"), key: "contentDeliveryType" }
    ];

    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });

    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const handleShowFilters = () => {
        dispatch(setHideFilter({ table: 'claims', hide: false }));
        setIsFilterVisible(!isFilterVisible);
    };
    // const handleDelete = async (reportDefinition: ReportDefinitionListItemDto) => {
    //     openDeleteModal(
    //         reportDefinition,
    //         IMPROVEMENT_PLAN_MODAL_HEADERS.titleDelete,
    //         `${IMPROVEMENT_PLAN_MODAL_HEADERS.subtitleDelete}: ${reportDefinition.title}?`,
    //         "deleteWithoutInput",
    //         ""
    //     );
    //     setSelectedDeleteReasonId(null);
    //     setDeleteModalError(undefined);
    //     setIsLoadingDeleteReasons(true);

    //     try {
    //         const options = await getReportDefinitionDeleteReasonOptions();
    //         setDeleteReasonOptions(options);
    //     } catch (error) {
    //         console.log(error);
    //         setDeleteModalError(t("common:requestError.errorBadRequest"));
    //     } finally {
    //         setIsLoadingDeleteReasons(false);
    //     }
    // };

    const closeDeleteModal = () => {
        closeDeleteModalBase();
        setSelectedDeleteReasonId(null);
        setDeleteModalError(undefined);
        setDeleteReasonOptions([]);
    };

    const confirmDelete = async () => {
        if (!selectedDeleteItem) {
            return;
        }

        if (!selectedDeleteReasonId) {
            setDeleteModalError(t("common:form.requiredFieldForm"));
            return;
        }

        const selectedReason = deleteReasonOptions.find((option) => option.id === selectedDeleteReasonId);
        if (!selectedReason) {
            setDeleteModalError(t("common:requestError.errorBadRequest"));
            return;
        }

        setIsDeleting(true);

        try {
            await deleteLogicReportDefinition(selectedDeleteItem.id, {
                deleteReasonId: selectedReason.id
            });
            closeDeleteModal();
            queryClient.invalidateQueries({
                queryKey: ['getReportDefinitionIndicators'],
                exact: false,
                refetchType: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: ['reportDefinitionPieChart'],
                exact: false,
                refetchType: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: ['getReportDefinitionStatusAndTypeBarChart'],
                exact: false,
                refetchType: 'all',
            });
            navigate("/report-definitions", {
                state: { message: t("reportDefinition:modal.messageDeletedReportDefinitionSuccessfully"), shouldRefresh: true }
            });
        } catch (error) {
            console.log(error);
            setDeleteModalError(t("common:requestError.errorBadRequest"));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleExportCsv = async () => {
        try {
            await exportReportDefinitionsToCsv({
                pageNumber: 1,
                pageSize: 10,
                search: searchText,
                filterByCurrentUser: assignedToMeToggle,
                selectedOrganizationalStructureIds: []
            });
            showAlert({
                message: t("reportDefinition:exportCsvSuccess"),
                type: "success"
            });
        } catch (error) {
            showAlert({
                message: t("common:requestError.errorBadRequest"),
                type: "danger"
            });
        }
    };

    return (
        <>
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("reportDefinition:list.reportDefinitionTitle")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end gap-2 flex-wrap mt-2 mt-md-0">
                    <Button
                        variant="secondary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => navigate(`/admin/report-definitions/new`)}
                    >
                        {t("reportDefinition:button.new")}
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {error && (
                        <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <div className="d-flex align-items-center gap-2">
                        <div style={{ flex: 1 }}>
                            <Search
                                searchText={searchText || ''}
                                onChange={(text) => dispatch(setSearchText({ table: "reportDefinitions", text: text }))}
                                onSearch={handleSearchInput}
                                onClear={handleClear}
                            />
                        </div>
                        <Button variant="" onClick={handleShowFilters} aria-controls="collapse-filter-id" style={{ border: 0, padding: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '40px' }}
                            />
                        </Button>
                    </div>
                </Col>
            </Row>

            {filters && (
                <FiltersContainer
                    filters={filters}
                    isFilterVisible={isFilterVisible}
                    tableKey="reportDefinitions"
                    historicalKey="reportDefinitions"              
                />
            )}
            <AppTable<ReportDefinitionListItemDto>
                data={reportDefinitionsPagination?.items || []}
                columns={columns}
                emptyMessage={t("common:emptyMessage")}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={reportDefinitionsPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="reportDefinitions"
                sectionPermission={SectionPermissionTypeEnum.REPORT_DEFINITIONS}
                onView={(id: number) => { navigate(`/admin/report-definitions/${id}`); }}
                onExportCsv={handleExportCsv}
                // onDelete={handleDelete}
            />

            {(isListLoading || isPageLoading) && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
        </>
    );
}
